import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FetchNotesErrorList } from "../../../../redux/slices/noteserror.slice";
import { getSenderNameList, getNoteTypeList } from "../../../../api/list";
import { sortInterFace } from "../../../../utils/types/interFace";
import { formatDateMMDDYYYY } from "../../../../utils";
import { NotesState, NotesActions } from "../types";

interface UsePatientNotesReturn {
  state: NotesState;
  actions: NotesActions;
}

const usePatientNotes = (selectedPatient: any): UsePatientNotesReturn => {
  const dispatch: any = useDispatch();
  const notesData = useSelector(
    (state: any) => state?.NotesErrorList?.NotesErrorList || {}
  );

  const [loader, setLoader] = useState<boolean>(false);
  const [senderName, setSenderName] = useState<any>([]);
  const [selectedSenderName, setSelectedSenderName] = useState<string>("");
  const [selectedDos, setSelectedDos] = useState<string | null>("");
  const [noteType, setNoteType] = useState<any>([]);
  const [selectedNoteType, setSelectedNoteType] = useState<string>("");
  const [selectedRows, setSelectedRows] = useState<any>([]);
  const [sort, setSort] = useState<sortInterFace>({ created_at: "desc" });
  const [limit] = useState<number>(15);
  const [page, setPage] = useState<number>(1);
  const [totalPage, setTotalPage] = useState<number>(1);
  const [showAISummarizeModal, setShowAISummarizeModal] = useState<boolean>(false);
  const [showWarningModal, setShowWarningModal] = useState<boolean>(false);
  const [todoExtraction, setTodoExtraction] = useState<boolean>(false);

  // Fetch filter options on mount
  useEffect(() => {
    const fetchSenderNames = async () => {
      try {
        const result = await getSenderNameList();
        if (result?.result) {
          setSenderName(
            result.result.map((item: any) => ({ label: item, value: item }))
          );
        }
      } catch (error) {
        console.error("Error fetching sender names:", error);
      }
    };

    const fetchNoteTypes = async () => {
      try {
        const result = await getNoteTypeList();
        if (result?.result) {
          setNoteType(
            result.result.map((item: any) => ({ label: item, value: item }))
          );
        }
      } catch (error) {
        console.error("Error fetching note types:", error);
      }
    };

    fetchSenderNames();
    fetchNoteTypes();
  }, []);

  const fetchNotes = (
    load: boolean,
    sorting: any = {},
    pageNumber?: number,
    dos?: string | null,
    senderNameVal?: string | null,
    noteTypeVal?: string | null
  ) => {
    if (!selectedPatient) return;

    pageNumber && setPage(pageNumber);
    if (pageNumber || page === 1) {
      setLoader(load);
    }

    dispatch(
      FetchNotesErrorList({
        page: pageNumber || page,
        limit,
        sort_by:
          Object.keys(sorting).length !== 0
            ? Object.keys(sorting)[0]
            : "created_at",
        sort_dir:
          Object.keys(sorting).length !== 0
            ? sorting[Object.keys(sorting)[0]]
            : "desc",
        date_of_service: dos === null ? null : dos || selectedDos,
        sender_name:
          senderNameVal === null ? null : senderNameVal || selectedSenderName,
        notes_type:
          noteTypeVal === null ? null : noteTypeVal || selectedNoteType,
        patient_id: selectedPatient?.id,
      })
    ).then((res: any) => {
      setTotalPage(res?.payload?.result?.total_pages);
    });
  };
  
  // Fetch notes when patient changes
  useEffect(() => {
    if (selectedPatient) {
      setSelectedRows([]);
      setPage(1);
      fetchNotes(true, sort, 1);
    }
    // eslint-disable-next-line
  }, [selectedPatient]);
  
  useEffect(() => {
    setLoader(false);
  }, [notesData]);

  // Fetch notes on page change
  useEffect(() => {
    if (selectedPatient) {
      fetchNotes(true, sort);
    }
    // eslint-disable-next-line
  }, [page]);

  const handleSenderNameChange = (selectedOption: any) => {
    setSelectedSenderName(selectedOption ? selectedOption.value : "");
    fetchNotes(
      true,
      sort,
      1,
      selectedDos,
      selectedOption ? selectedOption.value : null,
      selectedNoteType
    );
  };

  const handleNoteTypeChange = (selectedOption: any) => {
    setSelectedNoteType(selectedOption ? selectedOption.value : "");
    fetchNotes(
      true,
      sort,
      1,
      selectedDos,
      selectedSenderName,
      selectedOption ? selectedOption.value : null
    );
  };

  const handleDosChange = (value: string) => {
    setSelectedDos(value);
    fetchNotes(true, sort, 1, formatDateMMDDYYYY(value));
  };

  const handleDosClear = () => {
    setSelectedDos(null);
    fetchNotes(true, sort, 1, null);
  };

  const handleSort = (key: string, value: string) => {
    setSort({ [key]: value });
    page === 1
      ? fetchNotes(false, { [key]: value })
      : fetchNotes(false, { [key]: value }, 1);
  };

  const handleNextPage = () => {
    setPage(page + 1);
  };

  const redirectNotesPage = (row: any) => {
    window.open(`/notes/${row?.id}`, "_blank");
  };

  const handleShowAISummarizeModal = (isTodoExtraction = false) => {
    setTodoExtraction(isTodoExtraction);
    const allNotesSelected =
      selectedRows.length === notesData.length && notesData.length > 0;

    if (allNotesSelected) {
      setShowWarningModal(true);
    } else {
      setShowAISummarizeModal(true);
    }
  };

  const handleTodoPrompt = () => {
    handleShowAISummarizeModal(true);
  };

  const handleCloseAISummarizeModal = () => {
    setShowAISummarizeModal(false);
    setTodoExtraction(false);
    setSelectedRows([]);
  };

  const handleCloseWarningModal = () => {
    setShowWarningModal(false);
    setTodoExtraction(false);
  };

  const handleContinueWarning = () => {
    setShowWarningModal(false);
    setShowAISummarizeModal(true);
  };

  return {
    state: {
      loader,
      notesData,
      senderName,
      noteType,
      selectedSenderName,
      selectedNoteType,
      selectedDos,
      selectedRows,
      sort,
      page,
      totalPage,
      showAISummarizeModal,
      showWarningModal,
      todoExtraction,
    },
    actions: {
      handleSenderNameChange,
      handleNoteTypeChange,
      setSelectedDos,
      handleDosChange,
      handleDosClear,
      setSelectedRows,
      handleSort,
      handleNextPage,
      redirectNotesPage,
      handleShowAISummarizeModal,
      handleTodoPrompt,
      handleCloseAISummarizeModal,
      handleCloseWarningModal,
      handleContinueWarning,
    },
  };
};

export default usePatientNotes;